import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Search, Checkbox, Avatar, Text, Divider } from '@vibe/core';
import { Team as TeamIcon, Person, Close } from '@vibe/icons';
import type { ScheduleItem } from '../../stores/scheduleStore';
import type { AssigneeEntry } from './CreateItemDialog';
import { getFieldMappingLocal, getSubitemFieldMappingLocal } from '../../api/board-config.api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserOption {
  id: number;
  name: string;
  avatarUrl?: string;
}

interface TeamOption {
  id: number;
  name: string;
  memberNames?: string[];
}

export interface TaskEditDialogProps {
  task: ScheduleItem;
  /** All schedule items (used to find all assignees of the same monday item) */
  allItems: ScheduleItem[];
  /** Resolved assignee name */
  assigneeName: string;
  /** Available assignees for reassignment */
  assigneeOptions: UserOption[];
  /** Available teams */
  teamOptions: TeamOption[];
  onClose: () => void;
  /** Save changes */
  onSave: (
    taskId: string,
    updates: {
      name?: string;
      assignees?: AssigneeEntry[];
      startDate?: string;
      endDate?: string;
      hours?: number;
      hoursPerDay?: number;
      hoursTotal?: number;
      status?: string;
    },
  ) => void;
  /** Remove from schedule (unplan) */
  onRemove: (taskId: string) => void;
  /** Open in monday.com */
  onOpenInMonday: (mondayItemId: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const TaskEditDialog: React.FC<TaskEditDialogProps> = ({
  task,
  allItems,
  assigneeName,
  assigneeOptions,
  teamOptions,
  onClose,
  onSave,
  onRemove,
  onOpenInMonday,
}) => {
  // ── Editable state ────────────────────────────────────────────────────────
  const [itemName, setItemName] = useState(task.name);
  const [selectedAssignees, setSelectedAssignees] = useState<AssigneeEntry[]>(() => {
    // Collect ALL assignees from sibling items sharing the same mondayItemId
    const siblings = allItems.filter((i) => i.mondayItemId === task.mondayItemId);
    const entries: AssigneeEntry[] = [];
    const seen = new Set<string>();
    for (const sib of siblings) {
      if (sib.assigneeId > 0) {
        // Person assignee — assignedTeamId (if set) is just a UI grouping, not a real assignment
        const key = `person-${sib.assigneeId}`;
        if (!seen.has(key)) {
          seen.add(key);
          entries.push({ id: sib.assigneeId, kind: 'person' });
        }
      } else if (sib.assignedTeamId && sib.assignedTeamId > 0) {
        // Team-only assignee (no person assigned, assigneeId === 0)
        const key = `team-${sib.assignedTeamId}`;
        if (!seen.has(key)) {
          seen.add(key);
          entries.push({ id: sib.assignedTeamId, kind: 'team' });
        }
      }
    }
    return entries;
  });
  const [startDate, setStartDate] = useState(task.startDate);
  const [endDate, setEndDate] = useState(task.endDate);

  // ── Field mapping — determine which fields are mapped ──────────────
  const fieldMapping = useMemo(() => {
    const bId = task.boardId ?? 0;
    return task.isSubitem ? getSubitemFieldMappingLocal(bId) : getFieldMappingLocal(bId);
  }, [task.boardId, task.isSubitem]);
  const hasHoursPerDayColumn = !!fieldMapping?.scheduledHoursPerDayColumnId;
  const hasHoursTotalColumn = !!fieldMapping?.scheduledHoursColumnId;
  const hasBothHoursColumns = hasHoursPerDayColumn && hasHoursTotalColumn;
  const hasAnyHoursColumn = hasHoursPerDayColumn || hasHoursTotalColumn;
  const hasStatusColumn = !!fieldMapping?.statusColumnId;

  // Track both hour fields separately so the dialog can show both when both columns are mapped
  const [hoursPerDay, setHoursPerDay] = useState(String(task.hoursPerDay || 0));
  const [hoursTotal, setHoursTotal] = useState(String(task.hoursTotal || 0));
  // Fallback single-field value (used when only one column is mapped)
  const singleHours = hasHoursPerDayColumn ? hoursPerDay : hoursTotal;
  const setSingleHours = hasHoursPerDayColumn ? setHoursPerDay : setHoursTotal;
  const [status, setStatus] = useState(task.status ?? '');

  // ── Assignee picker state ──────────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  const lowerSearch = searchValue.toLowerCase();

  const filteredUsers = useMemo(
    () => assigneeOptions.filter((u) => u.name.toLowerCase().includes(lowerSearch)),
    [assigneeOptions, lowerSearch],
  );

  const filteredTeams = useMemo(
    () => teamOptions.filter((t) => t.name.toLowerCase().includes(lowerSearch)),
    [teamOptions, lowerSearch],
  );

  const isSelected = useCallback(
    (id: number, kind: 'person' | 'team') =>
      selectedAssignees.some((a) => a.id === id && a.kind === kind),
    [selectedAssignees],
  );

  const toggleAssignee = useCallback(
    (id: number, kind: 'person' | 'team') => {
      setSelectedAssignees((prev) => {
        const exists = prev.some((a) => a.id === id && a.kind === kind);
        if (exists) return prev.filter((a) => !(a.id === id && a.kind === kind));
        return [...prev, { id, kind }];
      });
    },
    [],
  );

  const removeAssignee = useCallback(
    (id: number, kind: 'person' | 'team') => {
      setSelectedAssignees((prev) => prev.filter((a) => !(a.id === id && a.kind === kind)));
    },
    [],
  );

  /** Resolve display name for a selected assignee chip */
  const getAssigneeName = useCallback(
    (entry: AssigneeEntry) => {
      if (entry.kind === 'person') {
        return assigneeOptions.find((u) => u.id === entry.id)?.name ?? `User ${entry.id}`;
      }
      return teamOptions.find((t) => t.id === entry.id)?.name ?? `Team ${entry.id}`;
    },
    [assigneeOptions, teamOptions],
  );

  // Close picker when clicking outside
  const handlePickerBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setPickerOpen(false);
      setSearchValue('');
    }
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSave = () => {
    onSave(task.id, {
      name: itemName !== task.name ? itemName.trim() : undefined,
      assignees: selectedAssignees,
      startDate,
      endDate,
      ...(hasBothHoursColumns
        ? { hoursPerDay: Number(hoursPerDay) || 0, hoursTotal: Number(hoursTotal) || 0 }
        : { hours: Number(singleHours) || 0 }),
      // Only send status if it actually changed to avoid unnecessary refetch
      status: status !== (task.status ?? '') ? (status || undefined) : undefined,
    });
    onClose();
  };

  const handleRemove = () => {
    onRemove(task.id);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="rp-create-dialog-overlay" onClick={handleOverlayClick}>
      <div className="rp-create-dialog">
        {/* Header */}
        <div className="rp-create-dialog__header">
          <input
            className="rp-create-dialog__title-input"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
          <button className="rp-create-dialog__close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="rp-create-dialog__body">
          {/* Assignees – multi-select picker */}
          <div className="rp-create-dialog__field">
            <label>Assignees</label>
            <div
              className="rp-assignee-picker"
              ref={pickerRef}
              onBlur={handlePickerBlur}
              tabIndex={-1}
            >
              {/* Trigger / selected chips */}
              <div
                className="rp-assignee-picker__trigger"
                onClick={() => { setPickerOpen(true); }}
              >
                {selectedAssignees.length === 0 && (
                  <span className="rp-assignee-picker__placeholder">
                    Select users or teams...
                  </span>
                )}
                {selectedAssignees.map((entry) => (
                  <span
                    key={`${entry.kind}-${entry.id}`}
                    className="rp-assignee-picker__chip"
                  >
                    {entry.kind === 'team' ? (
                      <TeamIcon className="rp-assignee-picker__chip-icon" />
                    ) : (
                      <Person className="rp-assignee-picker__chip-icon" />
                    )}
                    <span className="rp-assignee-picker__chip-label">
                      {getAssigneeName(entry)}
                    </span>
                    <button
                      className="rp-assignee-picker__chip-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAssignee(entry.id, entry.kind);
                      }}
                      type="button"
                    >
                      <Close />
                    </button>
                  </span>
                ))}
              </div>

              {/* Dropdown panel */}
              {pickerOpen && (
                <div className="rp-assignee-picker__dropdown">
                  <div className="rp-assignee-picker__search">
                    <Search
                      size="small"
                      value={searchValue}
                      onChange={(value: string) => setSearchValue(value)}
                      placeholder="Search person or team..."
                      autoFocus
                    />
                  </div>

                  <Divider />

                  <div className="rp-assignee-picker__list">
                    {/* Users section */}
                    {filteredUsers.length > 0 && (
                      <>
                        <Text
                          type={Text.types.TEXT2}
                          className="rp-assignee-picker__section-title"
                        >
                          Users
                        </Text>
                        {filteredUsers.map((user) => (
                          <div
                            key={`user-${user.id}`}
                            className="rp-assignee-picker__option"
                            onClick={() => toggleAssignee(user.id, 'person')}
                          >
                            <Checkbox
                              checked={isSelected(user.id, 'person')}
                              onChange={() => toggleAssignee(user.id, 'person')}
                            />
                            {user.avatarUrl ? (
                              <Avatar
                                type="img"
                                src={user.avatarUrl}
                                size="small"
                                ariaLabel={user.name}
                              />
                            ) : (
                              <Avatar
                                type="text"
                                text={user.name}
                                size="small"
                                ariaLabel={user.name}
                              />
                            )}
                            <Text type={Text.types.TEXT2}>{user.name}</Text>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Teams section */}
                    {filteredTeams.length > 0 && (
                      <>
                        <Text
                          type={Text.types.TEXT2}
                          className="rp-assignee-picker__section-title"
                        >
                          Teams
                        </Text>
                        {filteredTeams.map((team) => (
                          <div
                            key={`team-${team.id}`}
                            className="rp-assignee-picker__option"
                            onClick={() => toggleAssignee(team.id, 'team')}
                          >
                            <Checkbox
                              checked={isSelected(team.id, 'team')}
                              onChange={() => toggleAssignee(team.id, 'team')}
                            />
                            <Avatar
                              type="icon"
                              icon={TeamIcon}
                              size="small"
                              ariaLabel={team.name}
                            />
                            <div className="rp-assignee-picker__option-info">
                              <Text type={Text.types.TEXT2}>{team.name}</Text>
                              {team.memberNames && team.memberNames.length > 0 && (
                                <Text
                                  type={Text.types.TEXT2}
                                  className="rp-assignee-picker__option-desc"
                                >
                                  {team.memberNames.join(', ')}
                                </Text>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {filteredUsers.length === 0 && filteredTeams.length === 0 && (
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-assignee-picker__empty"
                      >
                        No results found
                      </Text>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="rp-create-dialog__field">
            <label>Timeline</label>
            <div className="rp-create-dialog__row">
              <div className="rp-create-dialog__field">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="rp-create-dialog__field">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Hours fields (show both when both columns are mapped) */}
          {hasBothHoursColumns ? (
            <div className="rp-create-dialog__row">
              <div className="rp-create-dialog__field">
                <label>Original Estimate (total hours)</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={hoursTotal}
                  onChange={(e) => setHoursTotal(e.target.value)}
                />
              </div>
              <div className="rp-create-dialog__field">
                <label>Original Estimate (h/day)</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={hoursPerDay}
                  onChange={(e) => setHoursPerDay(e.target.value)}
                />
              </div>
            </div>
          ) : hasAnyHoursColumn ? (
            <div className="rp-create-dialog__field">
              <label>{hasHoursPerDayColumn ? 'Original Estimate (h/day)' : 'Original Estimate (total hours)'}</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={singleHours}
                onChange={(e) => setSingleHours(e.target.value)}
              />
            </div>
          ) : null}

          {/* Status (only when status column is mapped) */}
          {hasStatusColumn && (
            <div className="rp-create-dialog__field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">No status</option>
                <option value="Not Started">Not Started</option>
                <option value="Working on it">Working on it</option>
                <option value="Stuck">Stuck</option>
                <option value="Done">Done</option>
              </select>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="rp-create-dialog__footer">
          <div className="rp-create-dialog__footer-left">
            <Button
              kind={Button.kinds.SECONDARY}
              size={Button.sizes.SMALL}
              onClick={() => onOpenInMonday(task.mondayItemId)}
            >
              Open in monday.com
            </Button>
          </div>
          <div className="rp-create-dialog__footer-right">
            <Button
              kind={Button.kinds.PRIMARY}
              size={Button.sizes.SMALL}
              color={Button.colors.NEGATIVE}
              onClick={handleRemove}
            >
              Unplan
            </Button>
            <Button size={Button.sizes.SMALL} onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskEditDialog;
